import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Box, Card, CardActionArea, CardContent, CardMedia, Checkbox, Chip, Grid, IconButton, ListItemText, MenuItem, OutlinedInput, Select, Skeleton, Stack, Typography, debounce, } from '@mui/material';
import { Clear as ClearIcon, } from '@mui/icons-material';
import { uniq } from 'lodash-es';
import { useStore, } from '@mcn-platform/models';
import { observer } from 'mobx-react-lite';
import dayjs from 'dayjs';
import { WysiwygViewer } from '../../publisher/wysiwyg/WysiwygEditor';
import AssetImage from '../common/asset/AssetImage';
const columnMap = {
    3: {
        xs: 12,
        sm: 6,
        md: 4,
        lg: 3,
        xl: 2,
    },
    4: {
        xs: 12,
        sm: 4,
        md: 3,
        lg: 2,
        xl: 2,
    },
    6: {
        xs: 6,
        sm: 3,
        md: 2,
        lg: 2,
        xl: 1,
    },
    8: {
        xs: 4,
        sm: 3,
        md: 2,
        lg: 1,
        xl: 1,
    },
    12: {
        xs: 3,
        sm: 4,
        md: 1,
        lg: 1,
        xl: 1,
    },
};
const textConfigItems = {
    text1: {
        name: 'Show Text 1',
        description: 'Show the item info on the card',
        tokenKey: 'kickerText1Label',
    },
    text2: {
        name: 'Show Text 2',
        description: 'Show the item info on the card',
        tokenKey: 'kickerText2Label',
    },
    text3: {
        name: 'Show Text 3',
        description: 'Show the item info on the card',
        tokenKey: 'kickerText3Label',
    },
    text4: {
        name: 'Show Text 4',
        description: 'Show the item info on the card',
        tokenKey: 'kickerText4Label',
    },
    text5: {
        name: 'Show Text 5',
        description: 'Show the item info on the card',
        tokenKey: 'kickerText5Label',
    },
};
const richTextConfigItems = {
    richText1: {
        name: 'Show Rich Text 1',
        description: 'Show the item info on the card',
        tokenKey: 'kickerRichText1Label',
    },
    richText2: {
        name: 'Show Rich Text 2',
        description: 'Show the item info on the card',
        tokenKey: 'kickerRichText2Label',
    },
    richText3: {
        name: 'Show Rich Text 3',
        description: 'Show the item info on the card',
        tokenKey: 'kickerRichText3Label',
    },
    richText4: {
        name: 'Show Rich Text 4',
        description: 'Show the item info on the card',
        tokenKey: 'kickerRichText4Label',
    },
    richText5: {
        name: 'Show Rich Text 5',
        description: 'Show the item info on the card',
        tokenKey: 'kickerRichText5Label',
    },
};
export const fieldSettingsDefault = [
    {
        key: 'name',
        active: true,
    },
];
const fieldConfigItems = {
    name: {
        name: 'Show Name',
        description: 'Show kicker name on the card',
        tokenKey: 'kickerNameLabel',
    },
    endAt: {
        name: 'Show End At Date',
        description: 'Show end at date',
        tokenKey: 'kickerEndAtLabel',
    },
    ...textConfigItems,
    ...richTextConfigItems,
};
const getTypographyStyle = (index) => {
    switch (index) {
        case 0:
            return 'h4';
        case 1:
            return 'h5';
        case 2:
            return 'h6';
        // case 2:
        //   return 'h5';
        // case 3:
        //   return 'h6';
        default:
            return 'body1';
    }
};
const KickerCard = ({ kicker, fieldSettings, linkEnabled = true, imageFit, imageHeight, showCardBorder, }) => {
    const cardMedia = (_jsx(CardMedia, { children: _jsx(AssetImage, { asset: kicker.asset, customStyle: { width: '100%', height: `${imageHeight}px`, objectFit: imageFit }, size: "thumb400w" }) }));
    return (_jsxs(Card, { sx: {
            width: '100%',
            boxShadow: showCardBorder ? 'rgba(17, 17, 26, 0.05) 0px 1px 0px, rgba(17, 17, 26, 0.1) 0px 0px 8px !important' : 'none',
        }, children: [linkEnabled && kicker.link ? (_jsx(CardActionArea, { onClick: () => {
                    window?.open(kicker.link, '_blank');
                }, children: cardMedia })) : cardMedia, _jsx(CardContent, { sx: { pb: 1 }, children: fieldSettings.map((field, i) => {
                    const value = kicker[field.key];
                    if (field.active) {
                        if (field.key.startsWith('richText')) {
                            return (_jsx(WysiwygViewer, { value: value?.slate, id: field.key, showMore: 250 }, field.key));
                        }
                        if (value instanceof Date) {
                            return (_jsx(Typography, { variant: getTypographyStyle(i), children: value.toLocaleDateString() }, field.key));
                        }
                        return (_jsx(Typography, { variant: getTypographyStyle(i), children: value }, field.key));
                    }
                    return null;
                }) })] }));
};
const FacetSelectBox = ({ facet, values, selected, onChange, counts, }) => (_jsx(Select, { displayEmpty: true, multiple: true, onChange: (e) => onChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value), value: selected, input: _jsx(OutlinedInput, { id: "select-multiple-chip" }), inputProps: {
        sx: { py: 0 },
    }, renderValue: (valueList) => {
        if (!valueList || valueList.length === 0) {
            return facet;
        }
        return (_jsx(Box, { sx: { display: 'flex', flexWrap: 'wrap', rowGap: 0.5 }, children: valueList.map((value) => (_jsx(Chip, { label: value, sx: { margin: 0.5 } }, value))) }));
    }, sx: {
        flexGrow: 1,
    }, children: values?.map((value) => (_jsxs(MenuItem, { value: value, children: [_jsx(Checkbox, { checked: selected.indexOf(value) > -1 }), _jsx(ListItemText, { primary: `${value} (${counts[`${facet}:${value}`]})` })] }, value))) }));
const SearchBox = ({ value, onChange, }) => {
    const [latestValue, setLatestValue] = useState(value);
    const debounceOnChange = debounce(onChange, 500);
    const handleChange = (e) => {
        setLatestValue(e.target.value);
        debounceOnChange(e.target.value);
    };
    return (_jsx(OutlinedInput, { value: latestValue, onChange: handleChange, placeholder: "Search", sx: {
            flexGrow: 1,
        }, endAdornment: (_jsx(IconButton, { onClick: () => onChange(''), children: _jsx(ClearIcon, {}) })) }));
};
const SkeletonGrid = () => (_jsx(Grid, { container: true, spacing: 2, alignContent: "center", justifyContent: "left", sx: { py: 2 }, children: [...Array(4).keys()].map((i) => (_jsx(Grid, { item: true, xs: 12, sm: 6, md: 4, lg: 3, xl: 2, sx: {
            minWidth: '304px',
        }, children: _jsx(Skeleton, { variant: "rectangular", width: "100%", height: "285px" }) }, i))) }));
const getSortOrder = (sortOrder) => {
    switch (sortOrder) {
        case 'dateAsc':
            return { field: 'endAt', direction: 'asc' };
        case 'dateDesc':
            return { field: 'endAt', direction: 'desc' };
        case 'name':
        default:
            return { field: 'name', direction: 'asc' };
    }
};
const KickerGrid = ({ includeTag = KickerGrid.defaultProps.includeTag, filterPrefixTags = KickerGrid.defaultProps.filterPrefixTags, fieldSettings, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
editData, padding, backgroundColor, limitToPageWidth, imageFit = 'cover', imageHeight = 200, linkEnabled = true, searchEnabled = true, sortOrder = 'dateDesc', filterEndAt = true, columns = 4, showCardBorder = true, }) => {
    const [kickers, setKickers] = React.useState(undefined);
    const [facets, setFacets] = React.useState(undefined);
    const [counts, setCounts] = React.useState({});
    const [isLoading, setIsLoading] = React.useState(true);
    const [facetSelections, setFacetSelections,] = React.useState({});
    const [searchText, setSearchText] = React.useState('');
    const store = useStore();
    const { currentPortal, getCachedList, fetchCachedList, } = store;
    const activeFieldSettings = fieldSettings.filter((field) => field.active);
    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            setKickers(undefined);
            console.log('loading data', {
                includeTag,
                filterPrefixTags,
            });
            const facetSelectionFilter = [
                ...Object.entries(facetSelections).map(([facet, values]) => {
                    if (values.length === 0) {
                        return undefined;
                    }
                    return {
                        or: values.map((value) => ({ tags: { matchPhrase: `${facet}:${value}` } })),
                    };
                }).filter(Boolean),
            ];
            const endAtFilter = {
                not: {
                    endAt: { lt: dayjs().format('YYYY-MM-DD') },
                },
            };
            const searchFields = fieldSettings.filter((field) => (field.active && field.key !== 'endAt'));
            const searchFilter = {
                and: [
                    // { portalID: { eq: currentPortal.id } },
                    { tags: { match: includeTag } },
                    ...(searchText ? [{
                            or: searchFields.map((field) => ({ [field.key]: { matchPhrasePrefix: searchText } })),
                        }] : []),
                    ...facetSelectionFilter,
                    ...(filterEndAt ? [endAtFilter] : []),
                ],
            };
            const newList = getCachedList('kicker', searchFilter, getSortOrder(sortOrder));
            newList.setRequestedCount(1000);
            await fetchCachedList(newList);
            const newKickers = newList.items; // .toReversed();
            const allTags = newKickers.flatMap((kicker) => (kicker.tags.split(',') || []));
            const facetValues = uniq(allTags).reduce((acc, tag) => {
                const tagParts = tag.split(':');
                if (tagParts.length === 1) {
                    // not a facet tag
                    return acc;
                }
                const [facet, value] = tagParts;
                acc[facet] = acc[facet] || [];
                acc[facet].push(value);
                return acc;
            }, {});
            const tagCounts = allTags.reduce((acc, tag) => {
                acc[tag] = acc[tag] || 0;
                acc[tag] += 1;
                return acc;
            }, {});
            // console.log('Facet values', { facetValues, tagCounts });
            setFacets(facetValues);
            setCounts(tagCounts);
            setKickers(newKickers);
            setIsLoading(false);
        };
        loadData();
    }, [
        currentPortal?.id,
        facetSelections,
        fetchCachedList,
        fieldSettings,
        filterEndAt,
        filterPrefixTags,
        includeTag,
        getCachedList,
        searchText,
        sortOrder,
    ]);
    // empty if no category has been selected and no items have been selected
    // and no title has been set.
    // const isEmpty = !kickers || kickers.length === 0;
    // if (isEmpty && editData) {
    //   return (
    //     <PlaceHolder placeHolderName="Configure Kicker Grid" />
    //   );
    // }
    // console.log('KickerGrid', {
    //   facets,
    //   facetSelections,
    //   fieldSettings,
    // });
    const columnSpan = columnMap[columns] || columnMap[4];
    return (_jsxs(Box, { sx: {
            backgroundColor: { backgroundColor },
            p: (`${padding}px`),
            ...(limitToPageWidth && {
                maxWidth: 1200,
                marginX: 'auto',
                width: '100%',
            }),
        }, children: [searchEnabled && (_jsxs(Stack, { direction: "row", flexGrow: 1, spacing: 2, children: [_jsx(SearchBox, { value: searchText, onChange: setSearchText }), filterPrefixTags?.split(',').map((facet) => {
                        if (facets && (!facets[facet] || facets[facet]?.length === 0)) {
                            return null;
                        }
                        return (_jsx(FacetSelectBox, { facet: facet, values: (facets && facets[facet]) || [], selected: (facetSelections && facetSelections[facet]) || [], onChange: (values) => {
                                setFacetSelections({
                                    ...facetSelections,
                                    [facet]: values,
                                });
                            }, counts: counts }, facet));
                    })] })), isLoading && (_jsx(SkeletonGrid, {})), kickers?.length === 0 && (_jsx(Typography, { variant: "h4", sx: { p: 2 }, children: "No items found" })), (kickers?.length && kickers?.length > 0) ? (_jsx(Grid, { container: true, spacing: 2, justifyContent: "left", sx: { py: 2 }, children: kickers.map((kicker) => (_jsx(Grid, { item: true, xs: columnSpan.xs, sm: columnSpan.sm, md: columnSpan.md, lg: columnSpan.lg, xl: columnSpan.xl, sx: {
                        minWidth: `${100 / columns}%`,
                    }, children: _jsx(KickerCard, { kicker: kicker, fieldSettings: activeFieldSettings, linkEnabled: linkEnabled, imageFit: imageFit, imageHeight: imageHeight, showCardBorder: showCardBorder }) }, kicker.id))) })) : null] }));
};
KickerGrid.defaultProps = {
    includeTag: 'event',
    filterPrefixTags: 'Type,Category',
    // fieldSettings: fieldSettingsDefault,
    // viewAction: () => {},
    padding: 0,
    imageFit: 'cover',
    imageHeight: 200,
    linkEnabled: true,
    searchEnabled: true,
    sortOrder: 'dateDesc',
    columns: 4,
    filterEndAt: true,
    showCardBorder: true,
};
KickerGrid.meta = {
    group: 'display',
    name: 'Kicker Grid',
    description: 'Displays kickers from a category or group in a searchable grid.',
    props: [
        {
            key: 'includeTag',
            name: 'Include Tag',
            type: 'text',
            description: 'Kickers with this tag are included in the list.',
        },
        {
            key: 'filterPrefixTags',
            name: 'Filter Prefix Tags',
            type: 'tags',
            description: 'Tags with this prefix are used to create the facets to filter results.',
        },
        {
            key: 'fieldSettings',
            name: 'Fields',
            type: 'courseTabsConfig',
            description: 'Show and hide individual fields as well as control field order.',
            items: fieldConfigItems,
        },
        {
            key: 'searchEnabled',
            name: 'Search Enabled',
            type: 'boolean',
            description: 'Search and facets are displayed.',
        },
        {
            key: 'sortOrder',
            name: 'Sort Order',
            type: 'select',
            description: 'Display order for the kickers',
            values: ['name', 'dateAsc', 'dateDesc'],
            labels: ['Name', 'Date Ascending', 'Date Descending'],
        },
        {
            key: 'filterEndAt',
            name: 'Filter After End Date',
            type: 'boolean',
            description: 'Hide kickers after their end date.',
        },
        {
            key: 'imageFit',
            name: 'Image Fit',
            type: 'select',
            description: 'How the image should fit in the card',
            values: ['cover', 'contain'],
            labels: ['Cover', 'Contain'],
        },
        {
            key: 'linkEnabled',
            name: 'Link Enabled',
            type: 'boolean',
            description: 'Clicking the kicker should open a new tab to the kicker link.',
        },
        {
            key: 'columns',
            name: 'Items Per Row',
            type: 'select',
            values: [3, 4, 6, 8, 12],
            labels: ['3', '4', '6', '8', '12'],
            description: 'Number of items to display per row at full width',
        },
        {
            key: 'imageHeight',
            name: 'Image Height',
            type: 'number',
            description: 'Height of kicker image in pixels.',
        },
        {
            key: 'showCardBorder',
            name: 'Show Card Border',
            type: 'boolean',
            description: 'Show a shadow border around each kicker card.',
        },
        {
            key: 'limitToPageWidth',
            name: 'Limit Width',
            type: 'boolean',
            description: 'Limit the width of the stack to the maximum page width',
            group: 'style',
        },
        {
            key: 'layout',
            name: 'Layout',
            type: 'layout',
            description: 'Layout properties such as spacing and background color.',
            group: 'style',
        },
    ],
    defaultConfig: {
        type: 'component',
        component: 'KickerGridWidget',
        props: {
            includeTag: 'event',
            limitToPageWidth: true,
            fieldSettings: fieldSettingsDefault,
            filterPrefixTags: KickerGrid.defaultProps.filterPrefixTags,
            imageFit: 'cover',
            imageHeight: 200,
            linkEnabled: true,
            searchEnabled: true,
            sortOrder: 'dateDesc',
            filterEndAt: true,
            columns: 4,
            showCardBorder: true,
        },
    },
};
export default observer(KickerGrid);
//# sourceMappingURL=KickerGridWidget.js.map